// @ts-nocheck
import { supabase } from '@/lib/supabase';
import type { Playlist, PlaylistWithItems, PlaylistItem } from '@/types/supabase';

export interface CreatePlaylistInput {
  name: string;
  description?: string;
  is_default?: boolean;
}

export interface UpdatePlaylistInput {
  name?: string;
  description?: string;
  is_default?: boolean;
}

/**
 * Get all playlists for the current user
 */
export async function getUserPlaylists(userId: string): Promise<{
  data: Playlist[];
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase
      .from('playlists')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return {
      data: data || [],
      error: null,
    };
  } catch (error) {
    console.error('Error fetching playlists:', error);
    return {
      data: [],
      error: error as Error,
    };
  }
}

/**
 * Get a single playlist with its items and hymn details
 */
export async function getPlaylistWithItems(playlistId: string): Promise<{
  data: PlaylistWithItems | null;
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase
      .from('playlists')
      .select(
        `
        *,
        playlist_items (
          *,
          hymn:hymns (*)
        )
      `
      )
      .eq('id', playlistId)
      .order('position', { foreignTable: 'playlist_items', ascending: true })
      .single();

    if (error) {
      throw error;
    }

    return {
      data: data as PlaylistWithItems,
      error: null,
    };
  } catch (error) {
    console.error('Error fetching playlist with items:', error);
    return {
      data: null,
      error: error as Error,
    };
  }
}

/**
 * Get the default playlist for a user (or create one if it doesn't exist)
 */
export async function getOrCreateDefaultPlaylist(userId: string): Promise<{
  data: Playlist | null;
  error: Error | null;
}> {
  try {
    // First, try to get existing default playlist
    const { data: existingPlaylists } = await supabase
      .from('playlists')
      .select('*')
      .eq('user_id', userId)
      .eq('is_default', true)
      .limit(1);

    if (existingPlaylists && existingPlaylists.length > 0) {
      return {
        data: existingPlaylists[0],
        error: null,
      };
    }

    // Create default playlist if it doesn't exist
    const { data: newPlaylist, error } = await supabase
      .from('playlists')
      .insert({
        user_id: userId,
        name: '내 플레이리스트',
        description: '기본 플레이리스트',
        is_default: true,
      } as any)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data: newPlaylist,
      error: null,
    };
  } catch (error) {
    console.error('Error getting/creating default playlist:', error);
    return {
      data: null,
      error: error as Error,
    };
  }
}

/**
 * Create a new playlist
 */
export async function createPlaylist(
  userId: string,
  input: CreatePlaylistInput
): Promise<{
  data: Playlist | null;
  error: Error | null;
}> {
  try {
    // If this is set as default, unset other defaults first
    if (input.is_default) {
      await supabase
        .from('playlists')
        .update({ is_default: false } as any)
        .eq('user_id', userId)
        .eq('is_default', true);
    }

    const { data, error } = await supabase
      .from('playlists')
      .insert({
        user_id: userId,
        name: input.name,
        description: input.description,
        is_default: input.is_default || false,
      } as any)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data,
      error: null,
    };
  } catch (error) {
    console.error('Error creating playlist:', error);
    return {
      data: null,
      error: error as Error,
    };
  }
}

/**
 * Update playlist metadata
 */
export async function updatePlaylist(
  playlistId: string,
  userId: string,
  input: UpdatePlaylistInput
): Promise<{
  data: Playlist | null;
  error: Error | null;
}> {
  try {
    // If this is set as default, unset other defaults first
    if (input.is_default) {
      await supabase
        .from('playlists')
        .update({ is_default: false } as any)
        .eq('user_id', userId)
        .eq('is_default', true);
    }

    const { data, error } = await supabase
      .from('playlists')
      .update({
        name: input.name,
        description: input.description,
        is_default: input.is_default,
        updated_at: new Date().toISOString(),
      } as any)
      .eq('id', playlistId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data,
      error: null,
    };
  } catch (error) {
    console.error('Error updating playlist:', error);
    return {
      data: null,
      error: error as Error,
    };
  }
}

/**
 * Delete a playlist
 */
export async function deletePlaylist(
  playlistId: string,
  userId: string
): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    const { error } = await supabase
      .from('playlists')
      .delete()
      .eq('id', playlistId)
      .eq('user_id', userId);

    if (error) {
      throw error;
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error('Error deleting playlist:', error);
    return {
      success: false,
      error: error as Error,
    };
  }
}

/**
 * Add a hymn to a playlist
 */
export async function addHymnToPlaylist(
  playlistId: string,
  hymnId: number
): Promise<{
  data: PlaylistItem | null;
  error: Error | null;
}> {
  try {
    // Get current max position
    const { data: items } = await supabase
      .from('playlist_items')
      .select('position')
      .eq('playlist_id', playlistId)
      .order('position', { ascending: false })
      .limit(1);

    const nextPosition = items && items.length > 0 ? (items[0] as any).position + 1 : 0;

    const { data, error } = await supabase
      .from('playlist_items')
      .insert({
        playlist_id: playlistId,
        hymn_id: hymnId,
        position: nextPosition,
      } as any)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      data,
      error: null,
    };
  } catch (error) {
    console.error('Error adding hymn to playlist:', error);
    return {
      data: null,
      error: error as Error,
    };
  }
}

/**
 * Remove a hymn from a playlist
 */
export async function removeHymnFromPlaylist(
  playlistId: string,
  hymnId: number
): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    const { error } = await supabase
      .from('playlist_items')
      .delete()
      .eq('playlist_id', playlistId)
      .eq('hymn_id', hymnId);

    if (error) {
      throw error;
    }

    // Reorder remaining items
    await reorderAfterRemoval(playlistId);

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error('Error removing hymn from playlist:', error);
    return {
      success: false,
      error: error as Error,
    };
  }
}

/**
 * Clear all items from a playlist
 */
export async function clearPlaylist(playlistId: string): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    const { error } = await supabase
      .from('playlist_items')
      .delete()
      .eq('playlist_id', playlistId);

    if (error) {
      throw error;
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error('Error clearing playlist:', error);
    return {
      success: false,
      error: error as Error,
    };
  }
}

/**
 * Reorder playlist items
 */
export async function reorderPlaylistItems(
  playlistId: string,
  fromPosition: number,
  toPosition: number
): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    // Get all items
    const { data: items, error: fetchError } = await supabase
      .from('playlist_items')
      .select('*')
      .eq('playlist_id', playlistId)
      .order('position', { ascending: true });

    if (fetchError || !items) {
      throw fetchError || new Error('Failed to fetch items');
    }

    // Reorder in memory
    const reordered = [...items];
    const [moved] = reordered.splice(fromPosition, 1);
    reordered.splice(toPosition, 0, moved);

    // Update positions
    const updates = reordered.map((item: any, index: number) => ({
      id: item.id,
      position: index,
    }));

    // Batch update
    for (const update of updates) {
      await supabase
        .from('playlist_items')
        .update({ position: update.position } as any)
        .eq('id', update.id);
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error('Error reordering playlist items:', error);
    return {
      success: false,
      error: error as Error,
    };
  }
}

/**
 * Helper: Reorder items after removal to maintain sequential positions
 */
async function reorderAfterRemoval(playlistId: string): Promise<void> {
  const { data: items } = await supabase
    .from('playlist_items')
    .select('*')
    .eq('playlist_id', playlistId)
    .order('position', { ascending: true });

  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as any;
      if (item.position !== i) {
        await supabase
          .from('playlist_items')
          .update({ position: i } as any)
          .eq('id', item.id);
      }
    }
  }
}

/**
 * Check if a hymn is in a playlist
 */
export async function isHymnInPlaylist(
  playlistId: string,
  hymnId: number
): Promise<{
  exists: boolean;
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase
      .from('playlist_items')
      .select('id')
      .eq('playlist_id', playlistId)
      .eq('hymn_id', hymnId)
      .limit(1);

    if (error) {
      throw error;
    }

    return {
      exists: data ? data.length > 0 : false,
      error: null,
    };
  } catch (error) {
    console.error('Error checking if hymn in playlist:', error);
    return {
      exists: false,
      error: error as Error,
    };
  }
}
